import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReportSegmentsItem from './ReportSegmentsItem';
import Button from '../Button';
import { useTranslation } from '../ChildrenContext';

const defParamsSegment = {
    exclusive: false,
    values: []
};

const parseData = (segments) => {
    const list = [];
    const selected = [];

    for (let key in (segments || {})) {
        selected.push(key);
        list.push({
            ...segments[key],
            segment: key
        });
    }

    return { selected, list };
};

const buildSegmentsMap = (data) => {
    const segments = {};
    (data || []).forEach((item) => {
        if (item && item.segment && item.values && item.values.length) {
            segments[item.segment] = {
                exclusive: !!item.exclusive,
                values: item.values
            };
        }
    });
    return segments;
};

// Filter (segment) rows of a report page. Shared by /statistics/report and all /statistics/report-by-* pages.
// segmentKeys: segment types of the page (config.<page>.groupParamsSegment) - the empty "add filter" row
//   is shown while more can be added; also the fallback list when there are no translations
// commonControls: one Apply/Reset pair below all rows (Reset calls onReset); each filled row gets a
//   "remove filter" button that removes it from the form only - the request is sent by Apply
const ReportSegments = ({
    segments: propSegments,
    onAction: propOnAction,
    onUpdateDate: propOnUpdateDate,
    onReset: propOnReset,
    commonControls = false,
    loading,
    segmentKeys,
    reportComponent,
    translationKey
}) => {
    const t = useTranslation();
    const staticActions = t?.actions || {};
    const maxSegments = (segmentKeys || []).length;

    const ensureEmptySegment = useCallback((data, selected) => {
        const hasEmpty = selected.indexOf('') !== -1;
        if (selected.length < maxSegments && !hasEmpty) {
            return {
                data: [...data, { ...defParamsSegment, segment: '' }],
                selected: [...selected, '']
            };
        }
        return { data, selected };
    }, [maxSegments]);

    const [state, setState] = useState(() => {
        const parsed = parseData(propSegments);
        const ensured = ensureEmptySegment(parsed.list, parsed.selected);
        return {
            segments: propSegments,
            selected: ensured.selected,
            data: ensured.data
        };
    });

    const stateRef = useRef(state);
    stateRef.current = state;

    // Rows are rebuilt from props only when the page passes other segments (URL, reset, ...), i.e. when
    // propSegments change by value. Local edits (rows not applied yet) are never overwritten by the props
    // that are still the old ones: the page gets the segments only on Apply.
    const lastPropSegmentsRef = useRef(JSON.stringify(propSegments || {}));

    useEffect(() => {
        const propJson = JSON.stringify(propSegments || {});
        if (propJson === lastPropSegmentsRef.current) {
            return;
        }
        lastPropSegmentsRef.current = propJson;

        const current = stateRef.current;
        if (JSON.stringify(current.segments || {}) === propJson) {
            return;
        }

        const parsed = parseData(propSegments);
        const ensured = ensureEmptySegment(parsed.list, parsed.selected);
        const nextState = {
            segments: propSegments,
            selected: ensured.selected,
            data: ensured.data
        };
        stateRef.current = nextState;
        setState(nextState);
    }, [propSegments, ensureEmptySegment]);

    const numberOfFilters = state.selected.filter(Boolean).length;

    // side effects (onAction / onUpdateDate) run outside the setState updater
    const updateSegmentsState = useCallback((getNext, options = {}) => {
        const { skipAction = false, triggerUpdate = false } = options;
        const prevState = stateRef.current;
        const { data: nextData, selected: nextSelected } = getNext(prevState);
        const ensured = ensureEmptySegment(nextData, nextSelected);
        const segmentsMap = buildSegmentsMap(ensured.data);
        const nextState = {
            ...prevState,
            data: ensured.data,
            selected: ensured.selected,
            segments: segmentsMap
        };

        stateRef.current = nextState;
        setState(nextState);

        if (!skipAction) {
            propOnAction && propOnAction(segmentsMap);

            if (triggerUpdate && propOnUpdateDate) {
                propOnUpdateDate(nextState);
            }
        }
    }, [ensureEmptySegment, propOnAction, propOnUpdateDate]);

    const removeAt = (index) => (prevState) => ({
        data: prevState.data.filter((_, idx) => idx !== index),
        selected: prevState.selected.filter((_, idx) => idx !== index)
    });

    // per-row Reset (no commonControls): remove the row and reload
    const onDeleteSegment = useCallback((index) => {
        updateSegmentsState(removeAt(index), { triggerUpdate: true });
    }, [updateSegmentsState]);

    // "remove filter" (commonControls): remove the row from the form only
    const onRemoveSegment = useCallback((index) => {
        updateSegmentsState(removeAt(index), { skipAction: true });
    }, [updateSegmentsState]);

    const onUpdateDate = useCallback(() => {
        updateSegmentsState((prevState) => ({
            data: [...prevState.data],
            selected: [...prevState.selected]
        }), { triggerUpdate: true });
    }, [updateSegmentsState]);

    const onChangeSegment = useCallback((segmentData, index) => {
        updateSegmentsState(prevState => {
            const data = [...prevState.data];
            const selected = [...prevState.selected];

            data[index] = segmentData;
            selected[index] = segmentData.segment;

            return { data, selected };
        }, { skipAction: true });
    }, [updateSegmentsState]);

    // common Reset: clear the rows here too (rows not applied yet are not known to the page)
    const onReset = useCallback(() => {
        const ensured = ensureEmptySegment([], []);
        const nextState = { segments: {}, data: ensured.data, selected: ensured.selected };
        stateRef.current = nextState;
        setState(nextState);
        propOnReset && propOnReset();
    }, [ensureEmptySegment, propOnReset]);

    const usedSegments = useMemo(() => (state.data || []).map(item => item.segment).filter(Boolean), [state.data]);

    return (
        <div className="statistics__filter-wrapper">
            <div className="statistics__filter-items table-filter">
                {state.data.map((item, i) => (
                    <ReportSegmentsItem
                        key={item.segment || `empty-${i}`}
                        index={i}
                        {...item}
                        usedSegments={usedSegments}
                        onDelete={onDeleteSegment}
                        onRemove={onRemoveSegment}
                        onChangeSegment={onChangeSegment}
                        onUpdateDate={onUpdateDate}
                        loading={loading}
                        canDelete={numberOfFilters >= 1}
                        isPlaceholder={item.segment === ''}
                        hideControls={commonControls}
                        reportComponent={reportComponent}
                        segmentKeys={segmentKeys}
                        translationKey={translationKey}
                    />
                ))}
            </div>
            {commonControls ? (
                <div className="row statistics__controls">
                    <div className="statistics__btn-wrap statistics__btn-wrap--common">
                        <Button
                            extraClass={{'apply-btn': true}}
                            loading={loading}
                            text={staticActions.apply}
                            onClick={onUpdateDate}
                        />
                        <Button
                            extraClass={{'reset-btn': true}}
                            text={staticActions.reset}
                            onClick={onReset}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
};

ReportSegments.propTypes = {
    segments: PropTypes.object,
    onAction: PropTypes.func,
    onUpdateDate: PropTypes.func,
    onReset: PropTypes.func,
    commonControls: PropTypes.bool,
    loading: PropTypes.bool,
    segmentKeys: PropTypes.array,
    reportComponent: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    translationKey: PropTypes.string
};

ReportSegments.defaultProps = {
    segments: {},
    onAction: () => {},
    onUpdateDate: () => {},
    loading: false,
    segmentKeys: [],
    translationKey: 'statistics'
};

export default ReportSegments;
